import { ArrowRight } from "lucide-react"
import type { ReactNode } from "react"
import { Trans, getI18n } from "react-i18next"
import { type Artifact, entityDef } from "@/lib/defs"
import { useFormatNumber, useT } from "@/lib/i18n"
import { stringifyExact } from "@/lib/lossless"
import { asArray, asString, isRecord } from "@/lib/utils"
import { LifecycleDiagram } from "./lifecycle-diagram"
import { Badge } from "./ui/badge"

function Section({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  const formatNumber = useFormatNumber()
  return (
    <section className="mt-8 first:mt-0">
      <h3 className="mb-2 flex items-baseline gap-2 text-sm font-medium">
        {title}
        {count !== undefined ? <span className="text-xs font-normal text-subtle-foreground tabular">{formatNumber(count)}</span> : null}
      </h3>
      {children}
    </section>
  )
}

function Table({ head, rows, empty }: { head: readonly string[]; rows: readonly (readonly ReactNode[])[]; empty?: string }) {
  const t = useT()
  if (rows.length === 0) return <p className="text-sm text-subtle-foreground">{empty ?? t("common.noneDeclared")}</p>
  return (
    <div className="overflow-x-auto rounded-lg border border-border-subtle">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border-subtle bg-card text-start text-xs text-muted-foreground">
            {head.map((h) => (
              <th key={h} className="px-3 py-1.5 font-normal whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {rows.map((cells, i) => (
            <tr key={i} className="align-top">
              {cells.map((c, j) => (
                <td key={j} className="px-3 py-1.5">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const Code = ({ children }: { children: ReactNode }) => <code className="font-mono text-xs text-foreground/90">{children}</code>
/** A ZEN expression or other source text, wrapped rather than truncated. */
const Expr = ({ children }: { children: ReactNode }) => <code className="font-mono text-xs break-words whitespace-pre-wrap text-foreground/85">{children}</code>
const Muted = ({ children }: { children: ReactNode }) => <span className="text-subtle-foreground">{children}</span>

export function formatType(type: unknown): string {
  if (!isRecord(type)) return "unknown"
  const kind = asString(type.kind) ?? "unknown"
  if (kind === "enum") return `enum · ${asArray(type.values).length}`
  if (kind === "list" || kind === "array") return `list<${formatType(type.items ?? type.element)}>`
  const ref = asString(type.ref) ?? asString(type.target) ?? asString(type.name)
  return ref ? `${kind} ${ref}` : kind
}

function constraintsOf(p: Record<string, unknown>): string {
  const parts: string[] = []
  const c = isRecord(p.constraints) ? p.constraints : {}
  if (c.nullable === true) parts.push("nullable")
  for (const [k, v] of Object.entries(c)) if (k !== "nullable" && (typeof v === "string" || typeof v === "number" || typeof v === "boolean")) parts.push(`${k} ${String(v)}`)
  return parts.join(", ")
}

function EntitySummary({ artifact }: { artifact: Artifact }) {
  const t = useT()
  const def = entityDef(artifact)
  if (!def) return <GenericSummary artifact={artifact} />
  const body = def.body
  if (def.form === "catalog") {
    const types = asArray(body.propertyTypes).filter(isRecord)
    return (
      <Section title={t("definitionSummary.sharedPropertyTypes")} count={types.length}>
        <Table
          head={[t("definitionSummary.name"), t("definitionSummary.type"), t("definitionSummary.description")]}
          rows={types.map((t) => [<Code key="n">{asString(t.name) ?? "?"}</Code>, <span key="t" className="text-muted-foreground">{formatType(t.propertyType ?? t.type)}</span>, <span key="d" className="text-muted-foreground">{asString(t.description) ?? ""}</span>])}
        />
      </Section>
    )
  }
  const properties = asArray(body.properties).filter(isRecord)
  const links = asArray(body.links).filter(isRecord)
  const keys = asArray(body.identity ?? body.keys).filter(isRecord)
  const lifecycles = asArray(body.lifecycles).filter(isRecord)
  const implementsList = asArray(body.implements).flatMap((i) => (typeof i === "string" ? [i] : []))
  const extendsName = asString(body.extends) ?? (def.form === "extension" ? asString(body.target) : undefined)
  return (
    <>
      {extendsName || implementsList.length > 0 ? (
        <div className="mb-6 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {extendsName ? (
            <>
              {t("definitionSummary.extends")} <Badge tone="outline">{extendsName}</Badge>
            </>
          ) : null}
          {implementsList.length > 0 ? (
            <>
              {t("definitionSummary.implements")}
              {implementsList.map((i) => (
                <Badge key={i} tone="outline">
                  {i}
                </Badge>
              ))}
            </>
          ) : null}
        </div>
      ) : null}
      <Section title={t("definitionSummary.properties")} count={properties.length}>
        <Table
          head={[t("definitionSummary.property"), t("definitionSummary.type"), t("definitionSummary.constraints"), t("definitionSummary.value")]}
          rows={properties.map((p) => {
            // A computed property is derived from a ZEN expression and read-only; it has no default.
            const computed = asString(p.computed)
            return [
              <span key="id" className="flex flex-wrap items-center gap-1.5">
                <Code>{asString(p.id) ?? "?"}</Code>
                {computed !== undefined ? <Badge tone="primary">{t("definitionSummary.computedReadOnly")}</Badge> : null}
              </span>,
              <span key="t" className="text-muted-foreground">
                {formatType(p.type)}
              </span>,
              constraintsOf(p) || <Muted key="c">—</Muted>,
              computed !== undefined ? (
                <Expr key="d">= {computed}</Expr>
              ) : p.default !== undefined ? (
                <span key="d" className="text-muted-foreground">
                  {t("definitionSummary.default")} <Code>{stringifyExact(p.default)}</Code>
                </span>
              ) : (
                <Muted key="d">—</Muted>
              ),
            ]
          })}
        />
      </Section>
      {links.length > 0 ? (
        <Section title={t("definitionSummary.links")} count={links.length}>
          <Table
            head={[t("definitionSummary.link"), t("definitionSummary.target"), t("definitionSummary.cardinality"), t("definitionSummary.inverse")]}
            rows={links.map((l) => {
              const card = isRecord(l.cardinality) ? l.cardinality : {}
              return [
                <Code key="id">{asString(l.id) ?? "?"}</Code>,
                <Code key="t">{asString(l.target) ?? "?"}</Code>,
                <span key="c" className="text-muted-foreground">
                  {asString(card.from) ?? "?"} → {asString(card.to) ?? "?"}
                  {card.required === true ? t("definitionSummary.required") : ""}
                </span>,
                asString(l.inverse) ? <Code key="i">{asString(l.inverse)}</Code> : <Muted key="i">—</Muted>,
              ]
            })}
          />
        </Section>
      ) : null}
      {keys.length > 0 ? (
        <Section title={t("definitionSummary.keys")} count={keys.length}>
          <Table head={[t("definitionSummary.key"), t("definitionSummary.properties")]} rows={keys.map((k) => [<Code key="n">{asString(k.name) ?? "?"}</Code>, <Code key="p">{asArray(k.properties).join(", ")}</Code>])} />
        </Section>
      ) : null}
      {lifecycles.map((lc) => {
        const transitions = asArray(lc.transitions).filter(isRecord)
        return (
          <Section key={asString(lc.property) ?? "lifecycle"} title={t("definitionSummary.lifecycle", { property: asString(lc.property) ?? "?" })} count={transitions.length}>
            {asString(lc.description) ? <p className="mb-2 text-sm text-muted-foreground">{asString(lc.description)}</p> : null}
            <div className="mb-3">
              <LifecycleDiagram lifecycle={lc} id={`${artifact.artifactId}:${asString(lc.property) ?? ""}`} />
            </div>
            <Table
              head={[t("definitionSummary.transition"), t("definitionSummary.from"), t("definitionSummary.to"), t("definitionSummary.description")]}
              rows={transitions.map((t) => [
                <Code key="id">{asString(t.id) ?? "?"}</Code>,
                <span key="f" className="flex flex-wrap gap-1">
                  {asArray(t.from).map((f) => (
                    <Badge key={String(f)}>{String(f)}</Badge>
                  ))}
                </span>,
                <span key="to" className="flex items-center gap-1">
                  <ArrowRight className="rtl-mirror size-3 text-subtle-foreground" aria-hidden />
                  <Badge tone="primary">{asString(t.to) ?? "?"}</Badge>
                </span>,
                <span key="d" className="text-muted-foreground">
                  {asString(t.description) ?? ""}
                </span>,
              ])}
            />
          </Section>
        )
      })}
    </>
  )
}

/** A slice key: `triggerObject`, or `{property: {from, property}}` as `order.customer`. */
export function formatSliceKey(key: unknown): string {
  if (typeof key === "string") return key
  if (isRecord(key) && isRecord(key.property)) {
    const from = asString(key.property.from)
    const property = asString(key.property.property)
    if (from && property) return `${from}.${property}`
  }
  return stringifyExact(key)
}

function RuleSummary({ artifact }: { artifact: Artifact }) {
  const t = useT()
  const p = artifact.payload
  if (!isRecord(p.graph)) {
    return (
      <>
        <p className="mb-4 text-sm text-muted-foreground">{t("definitionSummary.legacyDecision", { version: asString(p.ruleVersion) ?? "1" })}</p>
        <GenericSummary artifact={artifact} />
      </>
    )
  }
  const graph = isRecord(p.graph) ? p.graph : {}
  const nodes = asArray(graph.nodes).filter(isRecord)
  const triggers = asArray(p.triggers).filter(isRecord)
  const slice = asArray(p.slice).filter(isRecord)
  return (
    <>
      <dl className="mb-2 grid grid-cols-[120px_1fr] gap-y-1.5 text-sm">
        <dt className="text-muted-foreground">{t("definitionSummary.rule")}</dt>
        <dd>
          <Code>{asString(p.ruleId) ?? "?"}</Code>
        </dd>
        <dt className="text-muted-foreground">{t("definitionSummary.form")}</dt>
        <dd>
          <Trans shouldUnescape tOptions={{ interpolation: { escapeValue: true } }} t={t} i18nKey="definitionSummary.ruleForm" values={{ version: asString(p.ruleVersion) ?? "?", contract: asString(p.contract) ?? "?" }} components={{ contract: <Code>{""}</Code> }} />
        </dd>
      </dl>
      <Section title={t("definitionSummary.triggers")} count={triggers.length}>
        <Table
          head={[t("definitionSummary.on"), t("findings.objectType"), t("definitionSummary.detail")]}
          rows={triggers.map((t) => [<Code key="on">{asString(t.on) ?? "?"}</Code>, <Code key="o">{asString(t.objectType) ?? "—"}</Code>, <Code key="d">{asString(t.transition) ?? asString(t.property) ?? ""}</Code>])}
        />
      </Section>
      <Section title={t("definitionSummary.slice")} count={slice.length}>
        <Table head={[t("definitionSummary.name"), t("findings.objectType"), t("definitionSummary.key")]} rows={slice.map((s) => [<Code key="n">{asString(s.name) ?? "?"}</Code>, <Code key="o">{asString(s.objectType) ?? "?"}</Code>, <Expr key="k">{formatSliceKey(s.key)}</Expr>])} />
      </Section>
      <Section title={t("definitionSummary.decisionGraph")} count={nodes.length}>
        <Table
          head={[t("definitionSummary.node"), t("definitionSummary.type"), t("definitionSummary.content")]}
          rows={nodes.map((n) => {
            const content = isRecord(n.content) ? n.content : {}
            const detail =
              n.type === "decisionTableNode"
                ? t("definitionSummary.tableNode", { count: asArray(content.rules).length, inputCount: asArray(content.inputs).length, outputCount: asArray(content.outputs).length, hitPolicy: asString(content.hitPolicy) ?? "?" })
                : n.type === "expressionNode"
                  ? t("definitionSummary.expressionNode", { count: asArray(content.expressions).length })
                  : ""
            return [
              <span key="n">{asString(n.name) ?? asString(n.id) ?? "?"}</span>,
              <Code key="t">{asString(n.type) ?? "?"}</Code>,
              <span key="c" className="text-muted-foreground">
                {detail}
              </span>,
            ]
          })}
        />
      </Section>
    </>
  )
}

function schemaProperties(schema: unknown): [string, string][] {
  if (!isRecord(schema) || !isRecord(schema.properties)) return []
  const required = new Set(asArray(schema.required).map(String))
  return Object.entries(schema.properties).map(([k, v]) => [k, `${isRecord(v) ? (asString(v.type) ?? "any") : "any"}${required.has(k) ? "" : "?"}`])
}

function ActionSummary({ artifact }: { artifact: Artifact }) {
  const t = useT()
  const p = artifact.payload
  const inputs = schemaProperties(p.inputSchema)
  const preconditions = asArray(p.preconditions).map(String)
  const commands = asArray(p.commands).filter(isRecord)
  const writes = asArray(p.writes).filter(isRecord)
  const effect = isRecord(p.effectProgram) ? p.effectProgram : null
  return (
    <>
      {asString(p.description) ? <p className="mb-6 max-w-[70ch] text-sm text-muted-foreground">{asString(p.description)}</p> : null}
      <Section title={t("definitionSummary.input")} count={inputs.length}>
        <Table head={[t("definitionSummary.field"), t("definitionSummary.type")]} rows={inputs.map(([k, t]) => [<Code key="k">{k}</Code>, <span key="t" className="text-muted-foreground">{t}</span>])} />
      </Section>
      <Section title={t("definitionSummary.preconditions")} count={preconditions.length}>
        {preconditions.length === 0 ? (
          <p className="text-sm text-subtle-foreground">{t("common.noneDeclared")}</p>
        ) : (
          <ul className="space-y-1">
            {preconditions.map((c) => (
              <li key={c} className="rounded-md border border-border-subtle bg-card px-3 py-1.5">
                <Expr>{c}</Expr>
              </li>
            ))}
          </ul>
        )}
      </Section>
      {effect ? (
        <Section title={t("definitionSummary.effect")}>
          <p className="text-sm">
            <Code>{asString(effect.kind) ?? "?"}</Code>
            {asString(effect.transition) ? (
              <span className="text-muted-foreground">
                <Trans shouldUnescape tOptions={{ interpolation: { escapeValue: true } }} t={t} i18nKey="definitionSummary.transitionEffect" values={{ transition: asString(effect.transition) ?? "", object: asString(effect.objectKind) ?? "", lifecycle: asString(effect.lifecycle) ?? "" }} components={{ transition: <Code>{""}</Code>, object: <Code>{""}</Code> }} />
              </span>
            ) : null}
          </p>
        </Section>
      ) : null}
      {commands.length > 0 ? (
        <Section title={t("definitionSummary.commands")} count={commands.length}>
          <Table head={[t("definitionSummary.connector"), t("definitionSummary.operation"), t("definitionSummary.target")]} rows={commands.map((c) => [<Code key="c">{asString(c.connector) ?? "?"}</Code>, <Code key="o">{asString(c.operation) ?? "?"}</Code>, <span key="t" className="text-muted-foreground">{asString(c.doctype) ?? asString(c.target) ?? ""}</span>])} />
        </Section>
      ) : null}
      {writes.length > 0 ? (
        <Section title={t("definitionSummary.writes")} count={writes.length}>
          <Table head={[t("findings.objectType"), t("definitionSummary.change")]} rows={writes.map((w) => [<Code key="e">{asString(w.entityKind) ?? "?"}</Code>, <span key="c" className="text-muted-foreground">{asString(w.change) ?? "?"}</span>])} />
        </Section>
      ) : null}
    </>
  )
}

function MappingSummary({ artifact }: { artifact: Artifact }) {
  const t = useT()
  const p = artifact.payload
  const source = isRecord(p.source) ? p.source : {}
  const maps = asArray(p.maps).filter(isRecord)
  return (
    <>
      <p className="mb-6 text-sm text-muted-foreground">
        <Trans shouldUnescape tOptions={{ interpolation: { escapeValue: true } }} t={t} i18nKey="definitionSummary.mappingDescription" values={{ schema: asString(source.schemaId) ?? "?", version: asString(source.version) ?? "" }} components={{ schema: <Code>{""}</Code>, version: <Code>{""}</Code> }} />
      </p>
      {maps.map((m) => {
        const src = isRecord(m.source) ? m.source : {}
        const target = isRecord(m.target) ? m.target : {}
        const props = asArray(m.properties).filter(isRecord)
        const lifecycles = asArray(m.lifecycles).filter(isRecord)
        return (
          <Section key={asString(m.id) ?? "map"} title={`${asString(src.dataset) ?? "?"} → ${asString(target.entity) ?? "?"}`} count={props.length}>
            <Table
              head={[t("definitionSummary.property"), t("definitionSummary.expression")]}
              rows={[
                ...props.map((pr) => [<Code key="p">{asString(pr.property) ?? "?"}</Code>, <Expr key="v">{asString(pr.value) ?? stringifyExact(pr.value)}</Expr>]),
                ...lifecycles.map((lc) => [<Code key="p">{t("definitionSummary.stateProperty", { property: asString(lc.property) ?? "?" })}</Code>, <Expr key="v">{asString(lc.state) ?? ""}</Expr>]),
              ]}
            />
          </Section>
        )
      })}
    </>
  )
}

function SourceSchemaSummary({ artifact }: { artifact: Artifact }) {
  const t = useT()
  const p = artifact.payload
  const source = isRecord(p.source) ? p.source : {}
  const fields = asArray(p.fields).filter(isRecord)
  return (
    <>
      <p className="mb-6 text-sm text-muted-foreground">
        <Trans shouldUnescape tOptions={{ interpolation: { escapeValue: true } }} t={t} i18nKey={asString(p.observedAt) ? "definitionSummary.sourceObserved" : "definitionSummary.source"} values={{ system: asString(source.system) ?? "?", version: asString(source.version) ?? "", instance: asString(source.instance) ?? "?", observedAt: asString(p.observedAt) }} components={{ system: <Code>{""}</Code>, instance: <Code>{""}</Code> }} />
      </p>
      <Section title={t("definitionSummary.fields")} count={fields.length}>
        <Table
          head={[t("definitionSummary.dataset"), t("definitionSummary.field"), t("definitionSummary.nativeType"), t("definitionSummary.candidate")]}
          rows={fields.map((f) => [
            <span key="d">{asString(f.dataset) ?? "?"}</span>,
            <Code key="n">{asString(f.name) ?? "?"}</Code>,
            <span key="t" className="text-muted-foreground">
              {asString(f.nativeType) ?? "?"}
            </span>,
            <span key="c" className="text-muted-foreground">
              {isRecord(f.typeCandidate) ? asString(f.typeCandidate.type) : ""}
            </span>,
          ])}
        />
      </Section>
    </>
  )
}

function describeCondition(c: unknown, t: ReturnType<typeof useT>): string {
  if (!isRecord(c)) return stringifyExact(c)
  if (isRecord(c.role)) return t("definitionSummary.roleCondition", { roles: `{${asArray(c.role.anyOf).join(", ")}}` })
  if (isRecord(c.rel)) return t("definitionSummary.relationshipCondition", { from: asString(c.rel.from) ?? "?", role: asString(c.rel.role) ?? "?" })
  return stringifyExact(c)
}

/**
 * An AccessIR rule's full condition: the mandatory scope match (principal, resource and request agree on
 * every listed dimension) and then its `allOf` conjuncts. An empty `allOf` is scope-only, never "always".
 */
export function describeRuleCondition(rule: Record<string, unknown>, t: ReturnType<typeof useT> = getI18n().getFixedT("en")): string {
  const dimensions = isRecord(rule.scope) ? asArray(rule.scope.dimensions).map(String) : []
  const scope = dimensions.length > 0 ? t("definitionSummary.scopeMatch", { dimensions: dimensions.join(" + ") }) : t("definitionSummary.scopeUnspecified")
  const conjuncts = asArray(rule.allOf).map((condition) => describeCondition(condition, t))
  return conjuncts.length > 0 ? conjuncts.reduce((left, right) => t("definitionSummary.conjunction", { left, right }), scope) : t("definitionSummary.scopeOnly", { scope })
}

function PolicySummary({ artifact }: { artifact: Artifact }) {
  const t = useT()
  if (!Array.isArray(artifact.payload.rules)) {
    // Obligation and approval policies are other PolicyRoot forms, not AccessIR.
    return <GenericSummary artifact={artifact} />
  }
  const rules = asArray(artifact.payload.rules).filter(isRecord)
  return (
    <>
      <p className="mb-6 text-sm text-muted-foreground">{t("definitionSummary.policyDescription")}</p>
      <Section title={t("definitionSummary.rules")} count={rules.length}>
        <Table
          head={[t("definitionSummary.rule"), t("definitions.kind.action"), t("definitionSummary.resource"), t("definitionSummary.when")]}
          rows={rules.map((r) => [
            <Code key="r">{asString(r.ruleId) ?? "?"}</Code>,
            <span key="a" className="flex flex-col gap-0.5">
              {asArray(r.actions).map((a) => (
                <Code key={String(a)}>{String(a)}</Code>
              ))}
            </span>,
            <Code key="t">{asString(r.resourceType) ?? "?"}</Code>,
            <span key="w" className="text-muted-foreground">
              {describeRuleCondition(r, t)}
            </span>,
          ])}
        />
      </Section>
    </>
  )
}

function GenericSummary({ artifact }: { artifact: Artifact }) {
  const t = useT()
  const scalars = Object.entries(artifact.payload).filter(([, v]) => typeof v === "string" || typeof v === "number" || typeof v === "boolean")
  const nested = Object.entries(artifact.payload).filter(([, v]) => typeof v === "object" && v !== null)
  return (
    <>
      <Table head={[t("definitionSummary.field"), t("definitionSummary.value")]} rows={scalars.map(([k, v]) => [<Code key="k">{k}</Code>, <Expr key="v">{String(v)}</Expr>])} empty={t("definitionSummary.noScalarFields")} />
      {nested.length > 0 ? (
        <p className="mt-4 text-sm text-subtle-foreground">
          {t("definitionSummary.structuredFields", { fields: nested.map(([k]) => k).join(", ") })}
        </p>
      ) : null}
    </>
  )
}

export function DefinitionSummary({ artifact }: { artifact: Artifact }) {
  switch (artifact.kind) {
    case "entity":
      return <EntitySummary artifact={artifact} />
    case "rule":
      return <RuleSummary artifact={artifact} />
    case "action":
      return <ActionSummary artifact={artifact} />
    case "mapping":
      return <MappingSummary artifact={artifact} />
    case "source_schema":
      return <SourceSchemaSummary artifact={artifact} />
    case "policy":
      return <PolicySummary artifact={artifact} />
    default:
      return <GenericSummary artifact={artifact} />
  }
}
